import type { DevControlPort } from "./DevControlPort";
import type { DevToolsConfig } from "./DevToolsConfig";
import type { Log } from "@slot/presentation";

/**
 * Dev hotkeys: pause toggle + single frame step over the `DevControlPort`, on a `window`
 * keydown listener (`KeyboardEvent.code`).
 */
export class DevHotkeys {
  private readonly cfg: DevToolsConfig;
  private readonly devControl: DevControlPort;
  private readonly log?: Log;
  private boundWindowKeyDown?: (e: KeyboardEvent) => void;

  public constructor(input: {
    config: DevToolsConfig;
    devControl: DevControlPort;
    log?: Log;
  }) {
    this.cfg = input.config;
    this.devControl = input.devControl;
    this.log = input.log;
  }

  public start(): void {
    if (!this.cfg.enabled) return;
    this.boundWindowKeyDown = (e: KeyboardEvent) => {
      const pause = e.code === this.cfg.pauseToggleKeyCode;
      if (!pause && e.code !== this.cfg.stepFrameKeyCode) return;
      // The overlays take any key as a press: a dev key must not skip or close what it pauses.
      // Bound at boot, so it runs before the overlays' window listeners.
      e.stopImmediatePropagation();
      if (pause) this.togglePause();
      else this.stepFrame();
    };
    window.addEventListener("keydown", this.boundWindowKeyDown, { passive: true });
  }

  public stop(): void {
    if (this.boundWindowKeyDown) {
      window.removeEventListener("keydown", this.boundWindowKeyDown);
      this.boundWindowKeyDown = undefined;
    }
  }

  private togglePause(): void {
    this.devControl.togglePause();
    this.log?.(`[DevHotkeys] pauseToggle -> paused=${this.devControl.isPaused()}`);
  }

  private stepFrame(): void {
    this.devControl.stepFrame();
    this.log?.("[DevHotkeys] stepFrame");
  }
}
